import { useContext, useEffect, useState } from 'react'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  exportCompIntelligence,
  getCompIntelligenceOverview,
  refreshCompIntelligence,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Table from 'react-bootstrap/Table'

import { EmptyContainer } from '../EmptyContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

const formatCount = (value) => Number(value || 0).toLocaleString()

const formatRate = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return `${(Number(value) * 100).toFixed(1)}%`
}

const KPI_FIELDS = [
  ['originallyDistributed', 'Originally distributed'],
  ['eligibleAtCutoff', 'Eligible at cutoff'],
  ['admitted', 'Admitted'],
  ['noShow', 'No-show'],
  ['released', 'Released'],
  ['claimUnknown', 'Claim unknown'],
]

function ReportTable({ columns, rows, emptyMessage }) {
  if (!rows.length) {
    return (
      <Card body className="card--md">
        <EmptyContainer style="center lg report">
          <p>{emptyMessage}</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table className="align-middle">
            <thead>
              <tr className="md">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`uppercase text-body${
                      column.align ? ` text-${column.align}` : ''
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="md-x">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={
                        column.align ? `text-${column.align}` : undefined
                      }
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}

export default function CompIntelligenceWrapper({ organization }) {
  const { orgPermissions } = useContext(UserContext)
  const orgPermissionsReady = orgPermissions?.length > 0
  const isSuperAdmin = AuthService.isSuperAdminOrganizer()
  const permissions = AuthService.getPermissions()
  const hasPermission =
    isSuperAdmin ||
    (orgPermissionsReady &&
      checkPermission(
        permissions,
        ORGANIZATION_PERMISSION_SLUGS.VIEW_COMP_INTELLIGENCE
      ))
  const canExport =
    isSuperAdmin ||
    (hasPermission &&
      checkPermission(
        permissions,
        ORGANIZATION_PERMISSION_SLUGS.EXPORT_COMP_INTELLIGENCE
      ))
  const [loading, setLoading] = useState(true)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(loading)

  const [filters, setFilters] = useState({
    eventUuid: '',
    programId: '',
    source: '',
  })
  const [overview, setOverview] = useState(null)
  const [error, setError] = useState('')
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (
      (!orgPermissionsReady && !isSuperAdmin) ||
      !hasPermission ||
      !organization?.uuid
    ) {
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError('')
    const requestFilters = {
      eventUuid: filters.eventUuid || undefined,
      programId: filters.programId || undefined,
      source: filters.source || undefined,
    }

    getCompIntelligenceOverview(organization.uuid, requestFilters)
      .then((overviewResponse) => {
        if (cancelled) return
        setOverview(overviewResponse.data)
      })
      .catch(() => {
        if (!cancelled)
          setError('Complimentary intelligence could not be loaded.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
        completeRefresh()
      })

    return () => {
      cancelled = true
    }
  }, [
    orgPermissionsReady,
    isSuperAdmin,
    hasPermission,
    organization?.uuid,
    filters.eventUuid,
    filters.programId,
    filters.source,
    refreshToken,
  ])

  if (!orgPermissionsReady && !isSuperAdmin) return null
  if (!hasPermission) return <NoPermissionsContainer />
  if (loading || !overview) {
    if (!loading && error) {
      return (
        <div className="p-3">
          <div className="alert alert-danger">{error}</div>
        </div>
      )
    }
    return <PageLoadingContainer style="without-sidebar" />
  }

  const counts = overview?.counts
  const rates = overview?.rates
  const status = overview?.status
  const noSnapshot =
    status?.schemaReady === false ||
    (!status?.lastRefreshedAt && !counts?.originallyDistributed)
  const noMatches = status?.lastRefreshedAt && !counts?.originallyDistributed

  const updateSnapshot = async () => {
    setUpdating(true)
    setError('')
    try {
      await refreshCompIntelligence(
        organization.uuid,
        filters.eventUuid || undefined
      )
      refresh()
    } catch (updateError) {
      setError(
        updateError?.response?.data?.error?.message ||
          'The snapshot could not be updated.'
      )
    } finally {
      setUpdating(false)
    }
  }

  const downloadExport = async () => {
    const response = await exportCompIntelligence(organization.uuid, {
      eventUuid: filters.eventUuid || undefined,
      programId: filters.programId || undefined,
      source: filters.source || undefined,
    })
    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = 'comp-intelligence.csv'
    link.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <div data-testid="comp-intelligence-report-section" className="p-3">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">Complimentary intelligence</h1>
          <p className="text-muted mb-0">
            Programs are buyer types marked complimentary, or categorized as
            comp or box office comp. Claim rate is eligible comps with was
            scanned, divided by eligible comps. Claimed show rate is the
            admitted share of those scanned comps.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={refresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={updateSnapshot}
            disabled={updating}
          >
            {updating ? 'Updating…' : 'Update snapshot'}
          </Button>
          {canExport ? (
            <Button variant="outline-primary" onClick={downloadExport}>
              Export
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert alert-danger mb-3">{error}</div> : null}

      {status ? (
        <div className="alert alert-secondary mb-3">
          Reconciliation: {status.reconciliation || 'pending'}. Last updated:{' '}
          {status.lastRefreshedAt
            ? new Date(status.lastRefreshedAt).toLocaleString()
            : 'not yet'}
          . Metric version {status.metricVersion}.
          {status.claimCoverage == null
            ? ' Claim coverage is not available.'
            : ` Claim coverage ${(status.claimCoverage * 100).toFixed(1)}%.`}
        </div>
      ) : null}

      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Select
            aria-label="Event"
            value={filters.eventUuid}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                eventUuid: event.target.value,
              }))
            }
          >
            <option value="">All events</option>
            {(overview?.events || []).map((event) => (
              <option key={event.eventUuid} value={event.eventUuid}>
                {event.eventName || event.eventUuid}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Select
            aria-label="Program"
            value={filters.programId}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                programId: event.target.value,
              }))
            }
          >
            <option value="">All programs</option>
            {(overview?.programs || []).map((program) => (
              <option key={program.programId} value={program.programId}>
                {program.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Select
            aria-label="Source"
            value={filters.source}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                source: event.target.value,
              }))
            }
          >
            <option value="">All sources</option>
            {(overview?.sources || []).map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {noSnapshot ? (
        <div
          className="alert alert-info mb-3"
          data-testid="comp-intelligence-empty"
        >
          No complimentary snapshot yet. Update the snapshot to build it from
          existing complimentary orders. This does not release or change
          tickets.
        </div>
      ) : null}
      {noMatches ? (
        <div className="alert alert-info mb-3">
          No complimentary tickets match these filters.
        </div>
      ) : null}

      <Row className="g-3 mb-4">
        {KPI_FIELDS.map(([key, label]) => (
          <Col key={key} xs={6} md={4} xl={2}>
            <Card body>
              <div className="text-muted small">{label}</div>
              <div className="h4 mb-0">{formatCount(counts?.[key])}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Utilization</div>
            <div className="h4 mb-0">{formatRate(rates?.utilization)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Claim rate</div>
            <div className="h4 mb-0">{formatRate(rates?.claimRate)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Claimed show rate</div>
            <div className="h4 mb-0">{formatRate(rates?.claimedShowRate)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">No-show rate</div>
            <div className="h4 mb-0">{formatRate(rates?.noShowRate)}</div>
          </Card>
        </Col>
      </Row>

      <h2 className="h5">By program</h2>
      <div className="mb-4">
        <ReportTable
          emptyMessage="No programs match these filters."
          columns={[
            { key: 'name', label: 'Buyer type' },
            { key: 'distributed', label: 'Distributed', align: 'end' },
            { key: 'eligible', label: 'Eligible', align: 'end' },
            { key: 'admitted', label: 'Admitted', align: 'end' },
            { key: 'utilization', label: 'Utilization', align: 'end' },
          ]}
          rows={(overview?.programs || []).map((program) => ({
            id: program.programId,
            name: program.name,
            distributed: formatCount(program.counts.originallyDistributed),
            eligible: formatCount(program.counts.eligibleAtCutoff),
            admitted: formatCount(program.counts.admitted),
            utilization: formatRate(program.rates.utilization),
          }))}
        />
      </div>
    </div>
  )
}
